import axios, { AxiosDefaults, AxiosInstance, AxiosRequestConfig } from "axios";
import { ExtUser } from "../authorizationService";
import { ApiLogger } from "../workers/Logger/interface";
import { AccessToken } from "../axiosCustomService/services/TokenDatabase/interface";
import AxiosLoggingMiddleware from "../AxiosMiddlewares/LoggerMiddleware";
import { decodeJwt } from "../axiosCustomService/services/TokenDatabase/helpers";
import { subscriptions } from "../subscriptions";
import { AxiosCacheMiddlewareConfig } from "../axiosCustomService/services/CacheDatabase/interface";

const namespace = "AxiosService";

// class axios service for axios
export class AxiosService {
  private instance: AxiosInstance;
  private caller: string | undefined;
  private _accessToken: string | undefined;
  private _decodedAccessToken: AccessToken | undefined;
  private _user: ExtUser | undefined;
  readonly loggerMiddleware: AxiosLoggingMiddleware | undefined;
  readonly #cacheConfig: AxiosCacheMiddlewareConfig | undefined;

  constructor(baseUrl: string, apiLogger?: ApiLogger, caller?: string, autoSubscribe?: boolean, cacheConfig?: AxiosCacheMiddlewareConfig, autoCall = false) {
    this.instance = axios.create();
    this.setBaseURL(baseUrl);
    this.caller = caller;
    apiLogger && (this.loggerMiddleware = new AxiosLoggingMiddleware(this.instance, apiLogger, caller || namespace));
    autoSubscribe && this.subscribeToUserChanges(autoCall);
  }

  // set instance defaults
  public setDefaults(defaults: AxiosDefaults): void {
    Object.assign(this.instance.defaults, defaults);
    if (defaults.headers) {
      Object.assign(this.instance.defaults.headers, defaults.headers);
    }
  }

  // set instance auth
  public setAuth(accessToken: string): void {
    this.instance.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  }

  public set user(user: ExtUser | undefined) {
    this._user = user;
    if (!user) return;
    if (user.access_token && this._accessToken !== user.access_token) {
      this.setAccessToken(user.access_token);
    }
  }

  public get user(): ExtUser | undefined {
    return this._user;
  }

  public get accessToken(): string | undefined {
    return this._accessToken;
  }

  public set accessToken(token: string | undefined) {
    this._accessToken = token;
  }

  public get decodedAccessToken(): AccessToken | undefined {
    return this._decodedAccessToken;
  }

  public set decodedAccessToken(token: AccessToken | undefined) {
    this._decodedAccessToken = token;
  }

  // Subscribe to user changes and update Axios service
  public subscribeToUserChanges = (autoCall = false) => {
    autoCall
      ? subscriptions.subscribeImmediate("User", this.caller || namespace, (user: ExtUser) => {
        this.user = user;
      }
      )
      : subscriptions.subscribe("User", this.caller || namespace, (user: ExtUser) => {
        this.user = user;
      });
  }

  public setAccessToken = async (accessToken: string): Promise<void> => {
    this.accessToken = accessToken;
    this.decodedAccessToken = decodeJwt(accessToken) as AccessToken | undefined;

    this.setAuth(accessToken);
  };

  // set base url
  public setBaseURL(url: string): void {
    this.instance.defaults.baseURL = url;
  }

  public setAxios(axios: AxiosInstance): void {
    this.instance = axios;
  }

  public getAxios(): AxiosInstance {
    return this.instance;
  }

  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }

  public async get<T>(url: string, params?: any): Promise<T> {
    const { data } = await this.instance.get<T>(url, { params });
    return data;
  }

  public async post<T>(url: string, body?: any): Promise<T> {
    const { data } = await this.instance.post<T>(url, body);
    return data;
  }

  public async put<T>(url: string, body?: any): Promise<T> {
    const { data } = await this.instance.put<T>(url, body);
    return data;
  }

  public async delete(url: string): Promise<void> {
    return await this.instance.delete(url);
  }

  public async patch<T>(url: string, body?: any): Promise<T> {
    const { data } = await this.instance.patch<T>(url, body);
    return data;
  }

  public head(url: string): Promise<any> {
    return this.instance.head(url);
  }

  public options(url: string): Promise<any> {
    return this.instance.options(url);
  }

  public request(config: AxiosRequestConfig): Promise<any> {
    return this.instance.request(config);
  }

  // get headers from the request
  public getHeaders(): any {
    return this.instance.defaults.headers;
  }

  // set headers to the request
  public setHeaders(config: AxiosRequestConfig, headers: any): AxiosRequestConfig {
    return {
      ...config,
      headers,
    };
  }

  public errorHandler(error: any): Promise<any> {
    return Promise.reject(error);
  }

  // add response interceptor
  public addResponseInterceptor(interceptorResponseCallback: any, interceptorErrorCallback: any): void {
    this.instance.interceptors.response.use(interceptorResponseCallback, interceptorErrorCallback);
  }

  // add request interceptor
  public addRequestInterceptor(interceptorRequestCallback: any, interceptorErrorCallback: any): void {
    this.instance.interceptors.request.use(interceptorRequestCallback, interceptorErrorCallback);
  }
}

export default AxiosService;
